/**
 * Tiny e-mail-typo detector for common consumer domains.
 *
 * Catches the typos that cause Lead → Kunde merges to fail because the
 * registration mail differs from the lead mail by 1-2 chars (e.g.
 * "aron@gmai.com" vs "aron@gmail.com").
 *
 * Strategy:
 *   1. Hard-coded lookup table of known mis-spellings → canonical domain.
 *      Fast, zero-allocation, no false positives.
 *   2. Levenshtein-distance ≤ 1 fallback against a small whitelist of the
 *      most popular German/Swiss e-mail providers. Stops at the first
 *      match — keeps the hint deterministic.
 *
 * Pure function. Returns ``null`` when no suggestion can be made.
 */

const KNOWN_TYPOS = {
  // Gmail family
  "gmai.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gmali.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cm": "gmail.com",
  "gmail.con": "gmail.com",
  // Yahoo
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yhaoo.com": "yahoo.com",
  // Hotmail / Outlook
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmaill.com": "hotmail.com",
  "outloook.com": "outlook.com",
  "outlok.com": "outlook.com",
  // German providers
  "gmx.dee": "gmx.de",
  "gmx.cm": "gmx.de",
  "web.dee": "web.de",
  "web.cm": "web.de",
  "t-onlin.de": "t-online.de",
  "tonline.de": "t-online.de",
  // Swiss providers
  "bluwin.ch": "bluewin.ch",
  "bluewi.ch": "bluewin.ch",
  "gmal.ch": "gmail.com",
};

const COMMON_DOMAINS = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com",
  "icloud.com", "me.com", "live.com",
  "gmx.de", "gmx.ch", "gmx.at", "web.de", "t-online.de", "freenet.de",
  "bluewin.ch", "hispeed.ch", "sunrise.ch", "swissonline.ch",
  "aon.at", "chello.at",
];

const lev = (a, b) => {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 1) return 99;
  const m = a.length;
  const n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = curr;
  }
  return prev[n];
};

/**
 * @param {string} email — raw input
 * @returns {{ suggestion: string, canonical_domain: string } | null}
 */
export const suggestEmailFix = (email) => {
  if (!email || typeof email !== "string") return null;
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at < 1 || at === trimmed.length - 1) return null;
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  if (!domain.includes(".")) return null;

  // 1) Direct typo lookup — strongest, no false positives
  if (KNOWN_TYPOS[domain]) {
    const canonical = KNOWN_TYPOS[domain];
    return { suggestion: `${local}@${canonical}`, canonical_domain: canonical };
  }

  // 2) Already canonical → nothing to suggest
  if (COMMON_DOMAINS.includes(domain)) return null;

  // 3) Fuzzy match against the canonical list
  let best = null;
  let bestDist = 99;
  for (const cand of COMMON_DOMAINS) {
    const d = lev(domain, cand);
    if (d > 0 && d <= 1 && d < bestDist) {
      best = cand;
      bestDist = d;
    }
  }
  if (best) {
    return { suggestion: `${local}@${best}`, canonical_domain: best };
  }
  return null;
};

export default suggestEmailFix;
